import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, Row, Col, Table, Input, Label, ButtonGroup } from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import {
  createLabelPDF,
  THERMAL_PAPER_SIZES,
  setFontStyle,
} from "../../../../utils/pdfTicketUtils";
import axios from "axios";
import qrcode from "qrcode-js";
import JsBarcode from "jsbarcode";
import { v4 as uuidv4 } from "uuid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

function GeneradorEtiquetas() {
  const { user } = useContext(AuthContext);

  // URLs de API
  const URL_ARTICULOS = import.meta.env.VITE_URL_ARTICULOS;
  const URL_ARTICULOS_PROPORCION = import.meta.env
    .VITE_URL_ARTICULOS_PROPORCION;
  const URL_LOGS = import.meta.env.VITE_URL_LOGS;
  const URL_TALLAS = import.meta.env.VITE_URL_TALLAS;

  // Estados principales
  const [tallas, setTallas] = useState([]);
  const [customSizeOrder, setCustomSizeOrder] = useState([]);

  // Estados principales
  const [articulos, setArticulos] = useState([]);
  const [selectedArticulo, setSelectedArticulo] = useState("");
  const [articuloSeleccionado, setArticuloSeleccionado] = useState(null);

  // Estados para la tabla de proporción
  const [arrayColores, setArrayColores] = useState([]);
  const [arrayTallas, setArrayTallas] = useState([]);
  const [cantidades, setCantidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mostrarTabla, setMostrarTabla] = useState(false);

  // Cargar artículos al montar el componente
  useEffect(() => {
    cargarArticulos();
    axios
      .get(URL_TALLAS, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((res) => {
        const allTallas = res.data;
        setTallas(allTallas);

        const customeSizeOrder = allTallas.map((t) => t.name);
        setCustomSizeOrder(customeSizeOrder);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  // Cargar proporción cuando se selecciona un artículo
  useEffect(() => {
    if (selectedArticulo) {
      seleccionarArticulo();
    } else {
      setMostrarTabla(false);
      setArticuloSeleccionado(null);
    }
  }, [selectedArticulo]);

  const cargarArticulos = async () => {
    try {
      const response = await axios.get(URL_ARTICULOS, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      });

      const articulosActivos = response.data
        .filter((a) => a.is_active === "Si")
        .sort((a, b) => (a.codigo > b.codigo ? 1 : -1));

      setArticulos(articulosActivos);
    } catch (error) {
      console.error("Error cargando artículos:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los artículos",
      });
    }
  };

  const seleccionarArticulo = async () => {
    if (!selectedArticulo) {
      Swal.fire({
        icon: "warning",
        title: "Selecciona un artículo",
        text: "Por favor selecciona un artículo de la lista",
      });
      return;
    }

    setLoading(true);
    try {
      // Buscar el artículo seleccionado
      const articulo = articulos.find((a) => a._id === selectedArticulo);
      setArticuloSeleccionado(articulo);

      // Obtener la proporción del artículo
      const response = await axios.get(
        `${URL_ARTICULOS_PROPORCION}Articulos/${selectedArticulo}`,
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      );

      const allProporcion = response.data.filter(
        (porcion) => porcion.cantidad !== "0"
      );

      if (allProporcion.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Sin proporción",
          text: "Este artículo no tiene proporción definida",
        });
        setLoading(false);
        return;
      }

      // Extraer colores únicos
      let uniqueColors = {};
      let coloresArray = allProporcion
        .map((i) => ({
          name: i.colores[0].name,
          id: i.colores[0]._id,
        }))
        .filter((el) => {
          if (el && !uniqueColors[el.id]) {
            uniqueColors[el.id] = true;
            return true;
          }
          return false;
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      setArrayColores(coloresArray);

      // Extraer tallas únicas y ordenarlas
      let uniqueTallas = {};
      let tallasArray = allProporcion
        .map((i) => ({
          name: i.tallas[0].name,
          id: i.tallas[0]._id,
        }))
        .filter((el) => {
          if (el && !uniqueTallas[el.id]) {
            uniqueTallas[el.id] = true;
            return true;
          }
          return false;
        })
        .sort((a, b) => {
          const indexA = customSizeOrder.indexOf(a.name);
          const indexB = customSizeOrder.indexOf(b.name);

          if (indexA === -1 && indexB === -1)
            return a.name.localeCompare(b.name);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;

          return indexA - indexB;
        });

      setArrayTallas(tallasArray);

      // Inicializar cantidades en 0
      const cantidadesIniciales = [];
      coloresArray.forEach((color) => {
        tallasArray.forEach((talla) => {
          cantidadesIniciales.push({
            id: uuidv4(),
            colorId: color.id,
            colorName: color.name,
            tallaId: talla.id,
            tallaNombre: talla.name,
            cantidad: 0,
          });
        });
      });

      setCantidades(cantidadesIniciales);
      setMostrarTabla(true);
    } catch (error) {
      console.error("Error cargando proporción:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo cargar la información del artículo",
      });
    } finally {
      setLoading(false);
    }
  };

  const options = articulos.map((option) => {
    const junta = option.codigo + " " + option.nombre;
    const firstLetter = option.codigo[0].toUpperCase();
    return {
      firstLetter: /[0-9]/.test(firstLetter) ? "0-9" : firstLetter,
      ...option,
      junta,
    };
  });

  const handleCantidadChange = (colorId, tallaId, value) => {
    const updatedCantidades = cantidades.map((item) => {
      if (item.colorId === colorId && item.tallaId === tallaId) {
        return { ...item, cantidad: parseInt(value) || 0 };
      }
      return item;
    });
    setCantidades(updatedCantidades);
  };

  const generarEtiquetasPDF = async () => {
    try {
      // Filtrar solo las cantidades mayores a 0
      const cantidadesValidas = cantidades.filter((item) => item.cantidad > 0);

      if (cantidadesValidas.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Sin cantidades",
          text: "Por favor ingresa al menos una cantidad mayor a 0",
        });
        return;
      }

      setLoading(true);

      // Obtener datos completos de proporción para los códigos EAN
      const response = await axios.get(
        `${URL_ARTICULOS_PROPORCION}Articulos/${selectedArticulo}`,
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      );

      const proporcionData = response.data.filter(
        (porcion) => porcion.cantidad !== "0"
      );

      // Función auxiliar para validar EAN
      const isValidEan = (ean) => {
        if (!ean || ean === "NA" || ean.trim() === "") return false;
        return true;
      };

      // Verificar EANs problemáticos antes de generar
      const eansProblematicos = cantidadesValidas.filter((item) => {
        const proporcionItem = proporcionData.find(
          (p) =>
            p.colores[0]._id === item.colorId &&
            p.tallas[0]._id === item.tallaId
        );
        const ean = proporcionItem?.EAN;
        return !isValidEan(ean);
      });

      if (eansProblematicos.length > 0) {
        const detalles = eansProblematicos
          .map((item) => `${item.colorName} - ${item.tallaNombre}`)
          .join("\n");

        const result = await Swal.fire({
          icon: "warning",
          title: "Códigos de barras faltantes",
          html: `<p>Las siguientes combinaciones no tienen código EAN válido:</p>
                 <pre style="text-align:left;max-height:200px;overflow:auto;background:#f4f4f4;padding:10px;">${detalles}</pre>
                 <p>Se usará el código del artículo como respaldo. ¿Deseas continuar?</p>`,
          showCancelButton: true,
          confirmButtonText: "Continuar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) {
          setLoading(false);
          return;
        }
      }

      // Crear el PDF con formato estandarizado para etiquetas (49mm x 25mm)
      const doc = createLabelPDF({
        width: THERMAL_PAPER_SIZES.LABEL_49x25.width,
        height: THERMAL_PAPER_SIZES.LABEL_49x25.height,
        orientation: "landscape",
      });

      // ===== TABLA RESUMEN COMENTADA =====
      // La primera página con tabla resumen ha sido deshabilitada
      // Si se necesita reactivar, descomentar la sección anterior

      // ===== GENERAR ETIQUETAS INDIVIDUALES =====

      // Variable para controlar si es la primera página (para no agregar página extra)
      let isFirstLabel = true;

      // Ordenar cantidades primero por color alfabéticamente, luego por talla según orden personalizado
      const sortedCantidades = [...cantidadesValidas].sort((a, b) => {
        // Primero ordenar por nombre de color
        const colorCompare = a.colorName.localeCompare(b.colorName);
        if (colorCompare !== 0) return colorCompare;

        // Luego ordenar por talla según orden personalizado
        const aIndex = customSizeOrder.indexOf(a.tallaNombre);
        const bIndex = customSizeOrder.indexOf(b.tallaNombre);
        return aIndex - bIndex;
      });

      // Función para sanitizar el código EAN/barcode
      // Elimina espacios, acentos y caracteres especiales que causan problemas con escáneres
      const sanitizeBarcode = (code) => {
        if (!code || code === "NA") return null;
        return code
          .toString()
          .normalize("NFD") // Descompone caracteres acentuados
          .replace(/[\u0300-\u036f]/g, "") // Elimina los acentos
          .replace(/[^a-zA-Z0-9]/g, "") // Solo permite alfanuméricos (sin guiones ni puntos para compatibilidad con escáneres)
          .toUpperCase()
          .trim();
      };

      // Generar etiquetas según las cantidades especificadas
      for (const item of sortedCantidades) {
        // Buscar el EAN correspondiente a este color-talla
        const proporcionItem = proporcionData.find(
          (p) =>
            p.colores[0]._id === item.colorId &&
            p.tallas[0]._id === item.tallaId
        );

        // Obtener el EAN y sanitizarlo
        const rawEan = proporcionItem?.EAN || articuloSeleccionado.codigo;
        const ean = sanitizeBarcode(rawEan);
        const precio1 = proporcionItem?.articulos[0]?.listaPrecioPieza || 0;

        // Verificar si el EAN es válido
        if (!ean || ean.length === 0) {
          console.warn(`EAN inválido para ${item.colorName} - ${item.tallaNombre}: "${rawEan}"`);
        }

        // Generar la cantidad solicitada de etiquetas para esta combinación
        for (let i = 0; i < item.cantidad; i++) {
          // Solo agregar página si no es la primera etiqueta (ya que no hay tabla resumen)
          if (!isFirstLabel) {
            doc.addPage();
          }
          isFirstLabel = false;

          // MODELO - parte superior (tamaño moderado pero visible)
          setFontStyle(doc, 13, "bold");
          doc.text(`${articuloSeleccionado.codigo}`, 2, 4);

          // Nombre del producto - debajo del modelo
          setFontStyle(doc, 6, "bold");
          doc.text(`${articuloSeleccionado.nombre}`, 2, 6.5);

          // Generar código de barras con alta calidad - maximizado
          try {
            if (!ean || ean.length === 0) {
              throw new Error("EAN vacío o inválido");
            }
            const barcodeCanvas = document.createElement("canvas");
            JsBarcode(barcodeCanvas, ean, {
              format: "CODE128",
              width: 4, // Barras más gruesas para mejor lectura
              height: 80, // Altura en píxeles maximizada
              displayValue: false,
              marginTop: 0,
              marginBottom: 0,
              marginLeft: 0,
              marginRight: 0,
              background: "#FFFFFF",
              lineColor: "#000000",
            });
            const barcodeDataUrl = barcodeCanvas.toDataURL("image/png", 1.0);

            // Código de barras en el centro
            doc.addImage(barcodeDataUrl, "PNG", 3, 7, 40, 8);
          } catch (barcodeError) {
            console.warn("Error generando código de barras:", barcodeError, "EAN:", rawEan);
            setFontStyle(doc, 6, "normal");
            doc.text(`ERROR: EAN inválido "${rawEan}"`, 2, 12);
          }

          // Color y talla debajo del código de barras
          setFontStyle(doc, 6, "bold");
          const productText = `COLOR: ${item.colorName} | TALLA: ${item.tallaNombre}`;
          const productTextWidth = doc.getTextWidth(productText);
          const productCenterX = (46 - productTextWidth) / 2;
          doc.text(productText, Math.max(1, productCenterX), 16.5);

          // Precio - visible y prominente en la parte inferior derecha
          setFontStyle(doc, 9, "bold");
          doc.text(`MAY $${precio1.toFixed(0)}`, 28, 19.5);
        }
      }

      // Registrar en logs
      await axios.post(
        URL_LOGS,
        {
          tipo: "Generar Etiquetas",
          detalle: `${articuloSeleccionado.nombre} ${
            articuloSeleccionado.codigo
          } - Total: ${cantidadesValidas.reduce(
            (sum, item) => sum + item.cantidad,
            0
          )} etiquetas`,
          user: user.id,
        },
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      );

      // Generar salida PDF de alta calidad
      const pdfOutput = doc.output("blob", {
        compress: false, // Asegurar que no se aplique compresión a la salida final
      });
      const url = URL.createObjectURL(pdfOutput);
      window.open(url, "_blank");

      Swal.fire({
        icon: "success",
        title: "¡Etiquetas generadas!",
        text: `Se generaron ${cantidadesValidas.reduce(
          (sum, item) => sum + item.cantidad,
          0
        )} etiquetas`,
        timer: 2000,
      });

      // Limpiar selección y ocultar tabla
      setSelectedArticulo("");
      setArticuloSeleccionado(null);
      setMostrarTabla(false);
    } catch (error) {
      console.error("Error generando etiquetas:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron generar las etiquetas",
      });
    } finally {
      setLoading(false);
    }
  };

  const limpiarCantidades = () => {
    const cantidadesLimpias = cantidades.map((item) => ({
      ...item,
      cantidad: 0,
    }));
    setCantidades(cantidadesLimpias);
  };

  return (
    <>
      <Header />
      <br />
      <br />

      {user.menu_articulos ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuArticulos"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
          </Row>

          <h3 align="center">Generador de Etiquetas por Cantidad</h3>

          <Row>
            <Col md={4}>
              <Label className="mr-sm-2">Seleccionar Artículo</Label>
              <Autocomplete
                size="small"
                value={selectedArticulo}
                onChange={(event, selectedArticulo) => {
                  setSelectedArticulo(
                    selectedArticulo ? selectedArticulo._id : ""
                  );
                }}
                options={options.sort(
                  (a, b) => -b.firstLetter.localeCompare(a.firstLetter)
                )}
                groupBy={(option) => option.firstLetter}
                getOptionLabel={(option) => option?.junta || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selecciona"
                    variant="outlined"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    {option.codigo} {option.nombre}
                  </li>
                )}
              />
            </Col>
          </Row>

          {articuloSeleccionado && (
            <Row>
              <Col md={12}>
                <br />
                <div
                  style={{
                    backgroundColor: "#d1ecf1",
                    border: "1px solid #bee5eb",
                    borderRadius: "0.25rem",
                    padding: "0.75rem 1.25rem",
                    marginBottom: "1rem",
                  }}
                >
                  <h5>Artículo Seleccionado:</h5>
                  <p>
                    <strong>Código:</strong> {articuloSeleccionado.codigo}
                  </p>
                  <p>
                    <strong>Nombre:</strong> {articuloSeleccionado.nombre}
                  </p>
                  <p>
                    <strong>Piezas por Caja:</strong>{" "}
                    {articuloSeleccionado.piezasPorCaja}
                  </p>
                </div>
              </Col>
            </Row>
          )}

          {/* Tabla de cantidades */}
          {mostrarTabla && articuloSeleccionado && (
            <>
              <Row>
                <Col md={8}>
                  <h4>Cantidades por Color y Talla</h4>
                </Col>
                <Col md={4}>
                  <ButtonGroup id="logoutBoton">
                    <Button
                      size="sm"
                      className="btn"
                      color="warning"
                      onClick={limpiarCantidades}
                    >
                      Limpiar <i className="fas fa-eraser"></i>
                    </Button>
                    <Button
                      size="sm"
                      className="btn"
                      color="success"
                      onClick={generarEtiquetasPDF}
                      disabled={
                        loading ||
                        cantidades.every((item) => item.cantidad === 0)
                      }
                    >
                      {loading ? "Generando..." : "Generar PDF"}{" "}
                      <i className="far fa-file-pdf"></i>
                    </Button>
                    <Button
                      size="sm"
                      className="btn"
                      color="danger"
                      onClick={() => {
                        setMostrarTabla(false);
                        setSelectedArticulo("");
                        setArticuloSeleccionado(null);
                      }}
                    >
                      Cerrar <i className="fas fa-times"></i>
                    </Button>
                  </ButtonGroup>
                </Col>
              </Row>

              <div className="bg-gray-50 p-6 rounded-lg">
                <Label className="text-gray-700 font-medium mb-3 block">
                  Proporcion de Etiquetas
                </Label>

                {/* Contenedor con scroll horizontal */}
                <div
                  style={{
                    overflowX: "auto",
                    overflowY: "visible",
                    maxWidth: "100%",
                    border: "1px solid #e2e8f0",
                    borderRadius: "0.5rem",
                    backgroundColor: "white",
                  }}
                >
                  <Table
                    size="sm"
                    striped
                    borderless
                    className="mb-0"
                    style={{
                      minWidth: `${Math.max(
                        600,
                        150 + arrayTallas.length * 80
                      )}px`,
                      width: "auto",
                    }}
                  >
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#f8f9fa",
                        zIndex: 10,
                      }}
                    >
                      <tr className="border-b border-gray-200">
                        <th
                          className="py-3 text-left font-normal sticky-column"
                          style={{
                            width: "150px",
                            minWidth: "150px",
                            position: "sticky",
                            left: 0,
                            backgroundColor: "#f8f9fa",
                            zIndex: 20,
                            borderRight: "2px solid #dee2e6",
                          }}
                        >
                          Color / Talla
                        </th>
                        {arrayTallas.map((talla, index) => (
                          <th
                            key={talla.id}
                            className="py-3 text-center font-medium text-gray-600"
                            style={{
                              width: "80px",
                              minWidth: "80px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {talla.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {arrayColores.map((color, colorIndex) => (
                        <tr key={color.id} className="border-b border-gray-200">
                          <td
                            className="py-3 pr-4 text-gray-600 font-medium uppercase sticky-column"
                            style={{
                              width: "150px",
                              minWidth: "150px",
                              position: "sticky",
                              left: 0,
                              backgroundColor:
                                colorIndex % 2 === 0 ? "#f8f9fa" : "white",
                              zIndex: 10,
                              borderRight: "2px solid #dee2e6",
                            }}
                          >
                            {color.name}
                          </td>
                          {arrayTallas.map((talla) => {
                            const item = cantidades.find(
                              (c) =>
                                c.colorId === color.id && c.tallaId === talla.id
                            );
                            return (
                              <td
                                key={talla.id}
                                className="py-2 text-center"
                                style={{
                                  width: "80px",
                                  minWidth: "80px",
                                  padding: "8px 4px",
                                }}
                              >
                                <Input
                                  type="number"
                                  min="0"
                                  bsSize="sm"
                                  value={item?.cantidad || 0}
                                  onChange={(e) =>
                                    handleCantidadChange(
                                      color.id,
                                      talla.id,
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    width: "100%",
                                    textAlign: "center",
                                    fontSize: "12px",
                                    padding: "4px 8px",
                                  }}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Indicador de scroll si hay muchas columnas */}
                {arrayTallas.length > 8 && (
                  <div className="text-center mt-2">
                    <small className="text-muted">
                      <i className="fas fa-arrows-alt-h"></i> Desliza
                      horizontalmente para ver todas las tallas
                    </small>
                  </div>
                )}
              </div>

              <Row>
                <Col md={12}>
                  <br />
                  <div
                    style={{
                      backgroundColor: "#d1ecf1",
                      border: "1px solid #bee5eb",
                      borderRadius: "0.25rem",
                      padding: "0.75rem 1.25rem",
                      textAlign: "center",
                    }}
                  >
                    <strong>Total de etiquetas a generar: </strong>
                    <span
                      style={{
                        backgroundColor: "#007bff",
                        color: "white",
                        padding: "0.25rem 0.5rem",
                        borderRadius: "0.25rem",
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                      }}
                    >
                      {cantidades.reduce((sum, item) => sum + item.cantidad, 0)}
                    </span>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </div>
      ) : (
        <div className="container">
          <div
            style={{
              backgroundColor: "#f8d7da",
              border: "1px solid #f5c6cb",
              borderRadius: "0.25rem",
              padding: "0.75rem 1.25rem",
              textAlign: "center",
            }}
          >
            <h4>Acceso Denegado</h4>
            <p>No tienes permisos para acceder a esta sección.</p>
          </div>
        </div>
      )}
      <br />
    </>
  );
}

export default GeneradorEtiquetas;
